import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import PlusIcon from "../../../../../../images/products/plus.svg";
// import PlusWithCircle from '../../../../../../images/common/add-inside-circle-blue.svg';
import TickIcon from "../../../../../../images/common/tick-green.svg";
import CrossIcon from "../../../../../../images/products/cross-red.svg";
// import EditIcon from '../../../../../../images/products/edit-blue.svg';
import RecurringIcon from "../../../../../../images/common/subscribe-cycle-devy-grey.svg";
import CheckoutIFrame from "./CheckoutIFrame";
import {
  CheckOutItemsModel,
  CheckOutRecommendedItemDetails,
  CheckOutSellPageModel,
  CustomOptionModel,
  DonationPricingPageInputsModel,
  getPriceByBillingType,
  ItemDetailInputModel,
  RecurrencePriceSettings,
  RelatedItemsCategoriesModel,
} from "../../../shared/models";
import {
  AuthService,
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  Loader,
  SingleSelect,
  ToastService,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  CheckOutStore,
  PurchaseStore,
} from "../../../shared/store";
import { DonationService, ItemService } from "../../../shared/services";
import {
  CheckOutDisplayTypes,
  ItemTypes,
  SystemItemTypes,
} from "../../../shared/enums";
import {
  CheckoutDetailSection,
  CustomLine,
  IconAfterText,
  ProductDetailWrapper,
} from "../../../shared/styles";
import {
  CustomOptionPopUp,
  ItemHeader,
  SearchSelectInput,
} from "../../../shared/components";
// import AddIcon from '../../../../../../images/common/add-inside-circle-blue.svg';
import PlusWithCircle from "../../../../../../images/common/add-inside-circle-blue.svg";
import { firstValueFrom } from "rxjs";

type Props = {
  checkoutStore?: CheckOutStore;
  purchaseStore: PurchaseStore;
};

export interface ICheckoutItemOption {
  label: string;
  value: string;
  prices?: number;
}

export const recommendedCaptionTypes = [
  "Recommended",
  "Popular",
  "Featured",
  "Favorite",
  "TopRated",
];

const CheckOutItemsPage: React.FC<Props> = ({ checkoutStore }) => {
  const [selectedItem, setSelectedItem] =
    useState<RelatedItemsCategoriesModel | null>(null);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [showCustomOptionPopup, setShowCustomOptionPopup] = useState(false);

  useEffect(() => {
    if (checkoutStore && checkoutStore.sellPage.availableItems.length > 0) {
      setShowLoader(false);
      return;
    }
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await firstValueFrom(
        ItemService.getAvailableTypes(
          AuthService.authData.accountId,
          1,
          99999,
          "all",
          "",
          true,
        ),
      );
      if (response.result) {
        const mappedItems = RelatedItemsCategoriesModel.deserializeAPIList(
          response.result,
        );
        checkoutStore?.updateSellPage(
          new CheckOutSellPageModel({
            ...checkoutStore?.sellPage,
            availableItems: mappedItems,
          }),
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setShowLoader(false);
    }
  };

  const fetchDonationDetails = async (itemId: string) => {
    setShowLoader(true);

    try {
      if (checkoutStore?.detailPage.isDonationOnly) {
        const response = await DonationService.getDonation(
          AuthService.authData.accountId,
          itemId || "",
        ).toPromise();

        if (!response?.result) {
          ToastService.showError("Error in loading donation details");
        } else {
          const detailSectionData = ItemDetailInputModel.deserializeAPIModel(
            response.result,
          );
          const priceData = DonationPricingPageInputsModel.deserializeAPIModel(
            response.result.price,
          );

          checkoutStore?.updateDonationData(detailSectionData, priceData, true);
        }
      }
    } catch (error) {
      ToastService.showError("Error in loading donation details");
    } finally {
      setShowLoader(false);
    }
  };

  function updatePriceId(id: string, priceId: string) {
    const clonedItems = getClonedItems();
    const items = getClonedItems();
    const matchedItem = clonedItems.find((item) => item.id === id);
    if (!matchedItem) return;

    const priceIdExists = items.some((item) => item.priceId === priceId);
    if (priceIdExists) return;

    matchedItem.priceId = priceId;

    const newItems = [...items, matchedItem];
    checkoutStore?.updateSellPage(
      new CheckOutSellPageModel({
        ...checkoutStore?.sellPage,
        items: newItems,
      }),
    );
  }

  function onSelectPriceFromSubDropdown(id: string, priceId: string) {
    if (!checkoutStore?.sellPage) return;
    const matchedItem = checkoutStore.sellPage.availableItems.find(
      (item) => item.id === id,
    );

    const clonedItems = getClonedItems();
    if (!matchedItem) return;
    clonedItems.push(
      new CheckOutItemsModel({
        id: UtilityMethods.uniqueId(5),
        itemId: id,
        name: matchedItem?.name || "",
        quantity: 1,
        priceId: priceId,
        itemDetails: matchedItem,
        itemType: matchedItem?.type as SystemItemTypes,
      }),
    );
    checkoutStore?.updateSellPage(
      new CheckOutSellPageModel({
        ...checkoutStore?.sellPage,
        items: clonedItems,
      }),
    );
  }

  function onItemSelection(itemId: string) {
    if (!checkoutStore?.sellPage) return;
    const matchedItem = checkoutStore.sellPage.availableItems.find(
      (item) => item.id === itemId,
    );
    if (checkoutStore?.sellPage.items.length > 0) {
      const hasDonation = checkoutStore?.sellPage.items.some(
        (item) => item.itemDetails.type === ItemTypes.Donation,
      );
      if (hasDonation) {
        return;
      }
    }
    if (!matchedItem) return;

    if (matchedItem.type === "Donation") {
      fetchDonationDetails(itemId);
      const clonedItems = getClonedItems();
      clonedItems.push(
        new CheckOutItemsModel({
          id: UtilityMethods.uniqueId(5),
          itemId: itemId,
          name: matchedItem?.name || "",
          quantity: 1,
          priceId: "",
          itemDetails: matchedItem,
          itemType: matchedItem.type,
        }),
      );
      checkoutStore?.updateSellPage(
        new CheckOutSellPageModel({
          ...checkoutStore?.sellPage,
          items: clonedItems,
        }),
      );
      return;
    }
    let defaultPrice = matchedItem.prices?.find((p) => p.isDefaultPrice);
    if (!defaultPrice) {
      defaultPrice =
        matchedItem.prices?.length > 0 ? matchedItem.prices[0] : undefined;
    }

    if (!defaultPrice) return;
    const clonedItems = getClonedItems();
    clonedItems.push(
      new CheckOutItemsModel({
        id: UtilityMethods.uniqueId(5),
        itemId: itemId,
        name: matchedItem?.name || "",
        quantity: 1,
        priceId: defaultPrice.id,
        itemDetails: matchedItem,
      }),
    );

    checkoutStore?.updateSellPage(
      new CheckOutSellPageModel({
        ...checkoutStore?.sellPage,
        items: clonedItems,
      }),
    );
  }

  function getClonedItems() {
    const clonedItems =
      checkoutStore?.sellPage.items?.map(
        (item) => new CheckOutItemsModel(item),
      ) || [];
    return clonedItems;
  }

  function updateItem(
    id: string,
    priceId: string,
    key: keyof CheckOutItemsModel,
    value: string,
  ) {
    const clonedItems = getClonedItems();
    const matchedItem = clonedItems.find(
      (item) => item.id === id && item.priceId === priceId,
    );
    if (!matchedItem) return;

    switch (key) {
      case "name":
        matchedItem.name = value;
        break;
      case "quantity":
        matchedItem.quantity = parseInt(value);
        break;
    }
    checkoutStore?.updateSellPage(
      new CheckOutSellPageModel({
        ...checkoutStore?.sellPage,
        items: clonedItems,
      }),
    );
  }

  function deleteItem(id: string, priceId: string) {
    const clonedItems = getClonedItems();
    const matchedItem = clonedItems.find(
      (item) => item.id === id && item.priceId === priceId,
    );
    if (!matchedItem) return;
    const index = clonedItems.indexOf(matchedItem);
    clonedItems.splice(index, 1);
    checkoutStore?.updateSellPage(
      new CheckOutSellPageModel({
        ...checkoutStore?.sellPage,
        items: clonedItems,
      }),
    );
  }

  function updateSummaryFields(
    value: string | boolean,
    key: keyof CheckOutSellPageModel,
  ) {
    const clonedSellPage = new CheckOutSellPageModel(checkoutStore?.sellPage);
    switch (key) {
      case "groupByBillingFrequency":
        clonedSellPage.groupByBillingFrequency = value as boolean;
        break;
      case "recommendItem":
        clonedSellPage.recommendItem = value as boolean;
        if (!value) {
          clonedSellPage.recommendedItem = null;
        }
        if (value && !clonedSellPage.recommendedItem) {
          clonedSellPage.recommendedItem = new CheckOutRecommendedItemDetails();
        }
        break;
      case "allowIncludedItems":
        clonedSellPage.allowIncludedItems = value as boolean;
        break;
      case "allowRequiredItems":
        clonedSellPage.allowRequiredItems = value as boolean;
        break;
      case "allowRecommendedItems":
        clonedSellPage.allowRecommendedItems = value as boolean;
        break;
    }

    checkoutStore?.updateSellPage(clonedSellPage);
  }

  function updateRecommendedFields(
    value: string,
    key: keyof CheckOutRecommendedItemDetails,
  ) {
    if (!checkoutStore) return;
    const clonedSellPage = new CheckOutSellPageModel(checkoutStore?.sellPage);
    const selectedItem = checkoutStore?.sellPage?.items?.find(
      (item) => item.priceId === value,
    );
    const itemId = selectedItem?.itemId || "";

    if (!clonedSellPage.recommendedItem) {
      clonedSellPage.recommendedItem = new CheckOutRecommendedItemDetails(
        checkoutStore?.sellPage?.recommendedItem ||
          new CheckOutRecommendedItemDetails(),
      );
    }
    switch (key) {
      case "itemId":
        clonedSellPage.recommendedItem.itemId = itemId;
        clonedSellPage.recommendedItem.priceId = value;
        break;
      case "caption":
        clonedSellPage.recommendedItem.caption = value;
        break;
    }

    checkoutStore?.updateSellPage(clonedSellPage);
  }

  const options = (): ICheckoutItemOption[] => {
    if (!checkoutStore?.sellPage) return [];
    if (!checkoutStore?.detailPage.isDonationOnly) {
      return checkoutStore?.sellPage.availableItems
        .filter((item) => item.type !== "Donation")
        .map((item) => ({
          label: item.name,
          value: item.id,
          prices: item.prices.length,
        }))
        .sort((a, b) =>
          a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
        )
        .filter(
          (item) =>
            getClonedItems().findIndex((i) => i.itemId === item.value) === -1,
        );
    } else {
      return checkoutStore?.sellPage.availableItems
        .filter((item) => item.type === "Donation")
        .map((item) => ({
          label: item.name,
          value: item.id,
        }))
        .sort((a, b) =>
          a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
        )
        .filter(
          (item) =>
            getClonedItems().findIndex((i) => i.itemId === item.value) === -1,
        );
    }
  };

  function quantityRow(item: CheckOutItemsModel) {
    return (
      <>
        <FlexContainer spacing={8} alignItems="center" className="qty-input">
          <img
            src={DeleteIcon}
            onClick={() => deleteItem(item.id, item.priceId)}
            className="clickable"
          />
          <Input
            type="number"
            value={item.quantity}
            onChange={(e) =>
              updateItem(item.id, item.priceId, "quantity", e.target.value)
            }
            error={item.hasError("quantity")}
          />
          <img
            src={PlusIcon}
            alt="icon"
            onClick={() =>
              item.itemDetails.type === "Donation"
                ? {}
                : updateItem(
                    item.id,
                    item.priceId,
                    "quantity",
                    (item.quantity + 1).toString(),
                  )
            }
            className={
              item.itemDetails.type === "Donation" ? "not-allowed" : "clickable"
            }
          />
        </FlexContainer>
      </>
    );
  }

  function tickIcon(caption: string) {
    return (
      <div>
        <img src={TickIcon} className="yes-no-icon tick-icon" /> {caption}
      </div>
    );
  }

  function crossIcon(caption: string) {
    return (
      <div>
        <img src={CrossIcon} className="yes-no-icon" /> {caption}
      </div>
    );
  }

  function itemDetails(item: CheckOutItemsModel) {
    const itemDetails = item.itemDetails;
    if (itemDetails.type === ItemTypes.Donation)
      return (
        <>
          <FlexContainer
            spacing={0}
            fxWrap="wrap"
            padding="20px 0px 0px"
            className="yes-no-section"
          >
            {itemDetails.relatedItems
              ? tickIcon("Related Items")
              : crossIcon("Related Items")}
            {itemDetails.tax ? tickIcon("Allow Tax") : crossIcon("Allow Tax")}
          </FlexContainer>
          <CustomLine
            width="100%"
            height="1px"
            bgColor="#ddd"
            margin="20px 0px 20px"
          />
        </>
      );
    return (
      <>
        <FlexContainer
          spacing={0}
          fxWrap="wrap"
          padding="20px 0px 0px"
          className="yes-no-section"
        >
          {itemDetails.relatedItems
            ? tickIcon("Related Items")
            : crossIcon("Related Items")}
          {itemDetails.tax ? tickIcon("Allow Tax") : crossIcon("Allow Tax")}
          {itemDetails.discount
            ? tickIcon("Allow Discount")
            : crossIcon("Allow Discount")}
          {itemDetails.return
            ? tickIcon(`${itemDetails.returnDays} day returns`)
            : crossIcon("No returns")}
        </FlexContainer>
        <CustomLine
          width="100%"
          height="1px"
          bgColor="#ddd"
          margin="20px 0px 20px"
        />
      </>
    );
  }

  function checkBox(
    label: string,
    value: boolean,
    key: keyof CheckOutSellPageModel,
  ) {
    return (
      <div>
        <Input
          type="checkbox"
          value={value}
          onChange={(e) => updateSummaryFields(e.target.checked, key)}
          label={label}
        />
      </div>
    );
  }

  function recommendedOptions() {
    if (!checkoutStore?.sellPage?.items) return [];

    return checkoutStore?.sellPage.items.map((item) => {
      return {
        label:
          checkoutStore?.detailPage.selectedDisplayType ===
          "SingleItemWithMultiplePrices"
            ? item.itemDetails.prices.find((p) => p.id === item.priceId)
                ?.name || item.name
            : item.name,
        value: item.priceId,
        itemId: item.itemId,
      };
    });
  }

  function recommendCaptionTypes() {
    return recommendedCaptionTypes.map((c) => {
      return {
        label: c,
        value: c,
      };
    });
  }

  function recommendedSection() {
    if (!checkoutStore?.sellPage.recommendedItem) return;
    return (
      <>
        <FlexContainer spacing={15} padding="0px 0px 0px 30px">
          <div className="recommend-dropdown">
            <CustomLabel>Select Item / Price</CustomLabel>
            {singleSelect(
              checkoutStore?.sellPage.recommendedItem.priceId,
              recommendedOptions(),
              checkoutStore?.sellPage.recommendedItem?.hasError("itemId"),
              (value) => updateRecommendedFields(value, "itemId"),
            )}
          </div>
          {checkoutStore?.sellPage.recommendedItem.priceId && (
            <div className="recommend-dropdown">
              <CustomLabel>Select or Type</CustomLabel>
              {singleSelect(
                checkoutStore?.sellPage.recommendedItem.caption,
                recommendCaptionTypes(),
                checkoutStore?.sellPage.recommendedItem?.hasError("caption"),
                (value) => updateRecommendedFields(value, "caption"),
              )}
            </div>
          )}
        </FlexContainer>
      </>
    );
  }

  function singleSelect(
    selectedKey: string,
    options: IOption[],
    hasError: boolean,
    onChange: (value: string) => void,
  ) {
    return (
      <SingleSelect
        hasError={hasError}
        selectedKey={selectedKey}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options]}
        onChange={(option) => {
          onChange(option.value);
        }}
      />
    );
  }

  function bindPriceDropDown(
    selectedPriceId: string,
    id: string,
    item: RelatedItemsCategoriesModel,
  ) {
    if (item.type === "Product") return null;

    const existingPriceIds =
      checkoutStore?.sellPage.items.map((item) => item.priceId) || [];

    const options = item.prices
      .filter(
        (price) =>
          price.id === selectedPriceId || !existingPriceIds.includes(price.id),
      )
      .map((price) => {
        const priceDescriptions = getPriceByBillingType(
          item.type,
          item.hasVariants,
          item.variants,
          price.id,
          price.billingType,
          price.terms,
          price.recurrence,
          1,
        );

        return {
          label: price.name,
          value: price.id,
          name: item.name,
          priceDescription: priceDescriptions?.priceDescription,
          disabled: existingPriceIds.includes(price.id),
          isActive: price.isActive,
        };
      });

    const selectedOption = options.find(
      (option) => option.value === selectedPriceId,
    );
    const labelValue = selectedOption?.label || "";

    return (
      <SingleSelect
        selectedKey={labelValue}
        options={[options]}
        searchKeys={["label", "name"]}
        customHeader={
          <FlexContainer spacing={10} alignItems="center">
            <img src={RecurringIcon} className="sell-recurring-icon" />
            {labelValue}
          </FlexContainer>
        }
        customOption={(option) => (
          <FlexContainer
            padding="0px 0px 5px"
            key={option.value}
            style={{
              opacity: option.disabled ? 0.5 : 1,
              cursor: option.disabled ? "not-allowed" : "pointer",
            }}
            spacing={10}
            alignItems="center"
          >
            <div
              style={{
                height: "10px",
                width: "10px",
                borderRadius: "50%",
                backgroundColor: option.isActive ? "green" : "red",
              }}
            />
            <FlexContainer fxLayout="column">
              <span>{option.label}</span>
              <span>{option.priceDescription}</span>
            </FlexContainer>
          </FlexContainer>
        )}
        onChange={(option) => updatePriceId(id, option.value)}
        className="dpdn-with-icons dpdn-chckout-item"
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
      />
    );
  }

  function getRenewalTerms(recurrence: RecurrencePriceSettings) {
    const cancelDescription = "May cancel renewal at any time.";
    let renewalPriceDescription = "";

    if (recurrence.isIncreasePrice) {
      let increasePrice = "";
      switch (recurrence.increasePriceBy) {
        case "Amount":
          increasePrice = `$${recurrence.increasePriceValue}`;
          break;
        case "Percent":
          increasePrice = `${recurrence.increasePriceValue}%`;
          break;
      }
      switch (recurrence.increasePriceInterval) {
        case "Every6Month":
          renewalPriceDescription =
            <b>{increasePrice}</b> + " renewal increase every 6 months.";
          break;
        case "EveryYear":
          renewalPriceDescription =
            <b>{increasePrice}</b> + " renewal increase every year.";
          break;
      }
    }

    let renewalFeeDescription = "";

    if (recurrence.isRenewalFee) {
      renewalFeeDescription =
        <b>${recurrence.renewalFee}</b> + " additional fee on renewal.";
    }

    return (
      <FlexContainer spacing={20} padding="10px 0px 0px" fxLayout="column">
        <CustomLabel fontSize={"12px"} lineHeight="16px">
          <b>RENEWAL TERMS:</b> {cancelDescription}
        </CustomLabel>
        {renewalPriceDescription}
        {renewalFeeDescription}
      </FlexContainer>
    );
  }

  const renderItems = () => {
    return (
      <CollapsiblePanel
        title="Items"
        className="checkout-labels item-checkout-label"
      >
        {options().length === 0 &&
          checkoutStore?.sellPage.items.length === 0 && (
            <FlexContainer padding="20px 0px">
              <CustomLabel style={{ color: "red" }}>
                {checkoutStore?.detailPage.isDonationOnly
                  ? "No donation items available. Please create new donation items."
                  : "No items available. Please create new items before proceeding."}
              </CustomLabel>
            </FlexContainer>
          )}
        {checkoutStore?.sellPage.items.map((item, index) => {
          const price = item.itemDetails.prices.find(
            (p) => p.id === item.priceId,
          );
          const priceDescriptions = price
            ? getPriceByBillingType(
                item.itemDetails.type,
                item.itemDetails.hasVariants,
                item.itemDetails.variants,
                price.id,
                price.billingType,
                price.terms,
                price.recurrence,
                item.quantity,
              )
            : null;
          return (
            <div key={index}>
              <FlexContainer
                padding="0px 20px 0px 0px"
                spacing={20}
                justifyContent="space-between"
                // alignItems="center"
              >
                <div className="checkout-item-name">
                  <Input
                    onChange={(e) =>
                      updateItem(item.id, item.priceId, "name", e.target.value)
                    }
                    type="text"
                    value={item.name}
                    error={item.hasError("name")}
                  />
                  {item.itemType === ItemTypes.Donation && (
                    <img
                      // IconAfterText, use this component to display icon after text
                      src={DeleteIcon}
                      onClick={() => deleteItem(item.id, item.priceId)}
                      className="clickable"
                      alt="del"
                    />
                  )}
                  {/* <img src={EditIcon} className="clickable" /> */}
                </div>
                {priceDescriptions && (
                  <div>
                    <FlexContainer
                      fxLayout="column"
                      alignItems="flex-end"
                      justify-content="center"
                    >
                      {(priceDescriptions.originalPriceInNumber || 0) > 0 && (
                        <div className="red-strike">
                          {priceDescriptions.originalPrice}
                        </div>
                      )}
                      <div className="discounted-price">
                        {"$" +
                          AccountSettingsStore.formatAmount(
                            priceDescriptions.subTotal,
                          )}
                      </div>
                      <div className="renew-desc">
                        {priceDescriptions.renewDescription}
                      </div>
                    </FlexContainer>
                  </div>
                )}
              </FlexContainer>
              {item.itemDetails.prices.length > 1 &&
                item.itemDetails.type !== ItemTypes.Donation &&
                bindPriceDropDown(item.priceId, item.id, item.itemDetails)}
              {item.itemDetails.type !== ItemTypes.Donation &&
                quantityRow(item)}
              {item.itemDetails.type !== ItemTypes.Donation &&
                price?.recurrence &&
                getRenewalTerms(price.recurrence)}
              {itemDetails(item)}
            </div>
          );
        })}
      </CollapsiblePanel>
    );
  };

  const shouldShowSearchInput = () => {
    if (!checkoutStore) return false;
    const { detailPage, sellPage } = checkoutStore;
    const isSingleItem =
      detailPage.selectedDisplayType === CheckOutDisplayTypes.SingleItem;
    const isDonationOnly = detailPage.isDonationOnly;
    const hasOneItem = sellPage.items.length === 1;

    return !(isSingleItem && hasOneItem) && !(isDonationOnly && hasOneItem);
  };

  const onCustomOptionCreate = (customOption: CustomOptionModel) => {
    setShowCustomOptionPopup(false);
    checkoutStore?.updateSellPage(
      new CheckOutSellPageModel({
        ...checkoutStore?.sellPage,
        customoption: customOption,
      }),
    );
  };

  const hideSummarySection =
    checkoutStore?.detailPage.isDonationOnly ||
    checkoutStore?.detailPage.selectedDisplayType ===
      CheckOutDisplayTypes.SingleItem ||
    checkoutStore?.sellPage.items.length === 0;

  if (showLoader && (checkoutStore?.sellPage.items.length || 0) < 1)
    return <>{new Loader(true).loaderComponent}</>;
  if (!checkoutStore) return null;

  return (
    <ProductDetailWrapper>
      <FlexContainer>
        <CheckoutDetailSection width={"40%"}>
          <ItemHeader header="Sell" />
          {renderItems()}
          {shouldShowSearchInput() && (
            <FlexContainer fxLayout="column">
              {!showPriceDropdown && (
                <SearchSelectInput
                  options={options()}
                  hasError={checkoutStore?.sellPage.hasError("items")}
                  onChange={(option) => {
                    const selectedOption = options().find(
                      (opt) => opt.value === option.value,
                    );
                    if (
                      selectedOption &&
                      selectedOption.prices &&
                      selectedOption.prices > 1
                    ) {
                      setSelectedItem(
                        checkoutStore.sellPage.availableItems.find(
                          (item) => item.id === option.value,
                        ) || null,
                      );
                      setShowPriceDropdown(true);
                    } else {
                      onItemSelection(option.value.toString());
                    }
                  }}
                  customFooter={
                    checkoutStore?.detailPage.selectedDisplayType ===
                      CheckOutDisplayTypes.SingleItemWithMultiplePrices ||
                    checkoutStore?.detailPage.selectedDisplayType ===
                      CheckOutDisplayTypes.MultipleItems ? (
                      <FlexContainer spacing={15}>
                        <FlexContainer
                          alignItems="center"
                          onClick={() => setShowCustomOptionPopup(true)}
                        >
                          <img src={PlusWithCircle} />
                          <CustomLabel
                            fontSize="15px"
                            className="clickable"
                            color="#556BB8"
                          >
                            Custom Option
                          </CustomLabel>
                        </FlexContainer>
                      </FlexContainer>
                    ) : undefined
                  }
                  customOption={(
                    option: ICheckoutItemOption,
                    searchValue?: string,
                  ) => (
                    <FlexContainer
                      padding="5px 10px"
                      justifyContent="space-between"
                      alignItems="center"
                      className="search-dpdn-label"
                    >
                      <div>
                        {searchValue ? (
                          option.label
                            .split(new RegExp(`(${searchValue})`, "i"))
                            .map((part, partIndex) =>
                              part.toLowerCase() ===
                              searchValue.toLowerCase() ? (
                                <span
                                  key={partIndex}
                                  style={{ color: "#FF5900" }}
                                >
                                  {part}
                                </span>
                              ) : (
                                <>{part}</>
                              ),
                            )
                        ) : (
                          <div className="search-dpdn-label">
                            {option.label}
                          </div>
                        )}
                      </div>

                      {(option.prices || 0) > 0 && (
                        <div className="search-sub-label">
                          {option.prices}{" "}
                          {(option.prices || 0) > 1 ? "prices" : "price"}
                        </div>
                      )}
                    </FlexContainer>
                  )}
                  placeholder="Select Existing Item or Create New"
                />
              )}
              {showPriceDropdown &&
                (() => {
                  if (!selectedItem) return null;
                  if (selectedItem.type === "Product") return null;

                  const existingPriceIds =
                    checkoutStore?.sellPage.items.map((item) => item.priceId) ||
                    [];

                  const options = selectedItem.prices
                    .filter((price) => !existingPriceIds.includes(price.id))
                    .map((price) => {
                      const priceDescriptions = getPriceByBillingType(
                        selectedItem.type,
                        selectedItem.hasVariants,
                        selectedItem.variants,
                        price.id,
                        price.billingType,
                        price.terms,
                        price.recurrence,
                        1,
                      );

                      return {
                        label: price.name,
                        value: price.id,
                        name: selectedItem.name,
                        priceDescription: priceDescriptions?.priceDescription,
                        disabled: existingPriceIds.includes(price.id),
                        isActive: price.isActive,
                      };
                    });

                  return (
                    <FlexContainer
                      fxLayout="column"
                      size={0.3}
                      border="3px solid #9BC9FF"
                      className="search-selected-dpdn-menu"
                    >
                      <FlexContainer
                        alignItems="center"
                        spacing={15}
                        bgColor="#DDDDDD"
                        padding="10px 15px"
                      >
                        <i
                          className="fa fa-light fa-chevron-left clickable"
                          style={{ color: "#556BB8" }}
                          onClick={() => setShowPriceDropdown(false)}
                        ></i>
                        <CustomLabel fontSize={"15px"}>
                          {selectedItem?.name}
                        </CustomLabel>
                      </FlexContainer>
                      <FlexContainer fxLayout="column" spacing={5}>
                        {options.map((option) => (
                          <FlexContainer
                            padding="3px 10px 5px"
                            key={option.value}
                            style={{
                              opacity: option.disabled ? 0.5 : 1,
                              cursor: option.disabled
                                ? "not-allowed"
                                : "pointer",
                            }}
                            spacing={10}
                            alignItems="center"
                            onClick={() => {
                              setShowPriceDropdown(false);
                              onSelectPriceFromSubDropdown(
                                selectedItem.id,
                                option.value,
                              );
                            }}
                          >
                            <div
                              style={{
                                height: "10px",
                                width: "10px",
                                borderRadius: "50%",
                                backgroundColor: option.isActive
                                  ? "green"
                                  : "red",
                              }}
                            />
                            <FlexContainer fxLayout="column">
                              <div>{option.label}</div>
                              <div>{option.priceDescription}</div>
                            </FlexContainer>
                          </FlexContainer>
                        ))}
                      </FlexContainer>
                    </FlexContainer>
                  );
                })()}
            </FlexContainer>
          )}
          {!hideSummarySection && (
            <CollapsiblePanel
              title="Summary"
              className="checkout-labels labels-mt-35"
            >
              <FlexContainer
                spacing={20}
                fxLayout="column"
                className="checkout-checkboxes"
              >
                {" "}
                {checkoutStore?.detailPage.selectedDisplayType !==
                  CheckOutDisplayTypes.SingleItemWithMultiplePrices &&
                  checkBox(
                    "Group by Billing Frequency",
                    checkoutStore?.sellPage.groupByBillingFrequency || false,
                    "groupByBillingFrequency",
                  )}
                {checkoutStore?.sellPage.items.length > 0 && (
                  <>
                    {checkBox(
                      "Recommend an Item",
                      checkoutStore?.sellPage.recommendItem,
                      "recommendItem",
                    )}
                    {checkoutStore?.sellPage.recommendItem &&
                      recommendedSection()}
                  </>
                )}
              </FlexContainer>
            </CollapsiblePanel>
          )}
          <CollapsiblePanel title="Related Items" className="checkout-labels">
            <FlexContainer
              spacing={20}
              fxLayout="column"
              className="checkout-checkboxes"
            >
              {checkBox(
                "Allow Included Items",
                checkoutStore?.sellPage.allowIncludedItems,
                "allowIncludedItems",
              )}
              {checkBox(
                "Allow Required Items",
                checkoutStore?.sellPage.allowRequiredItems,
                "allowRequiredItems",
              )}
              {checkBox(
                "Allow Recommended Items",
                checkoutStore?.sellPage.allowRecommendedItems,
                "allowRecommendedItems",
              )}
            </FlexContainer>
          </CollapsiblePanel>
        </CheckoutDetailSection>
        {/* {!checkoutStore?.detailPage.isDonationOnly && (
                  <CheckoutPreviewSection>{getPreviewContent()}</CheckoutPreviewSection>
              )} */}
        <CheckoutIFrame checkoutStore={checkoutStore} />
      </FlexContainer>
      {showCustomOptionPopup && (
        <CustomOptionPopUp
          customOptionData={
            checkoutStore?.sellPage.customoption || new CustomOptionModel()
          }
          onSave={(data) => onCustomOptionCreate(data)}
          togglePopUp={() => setShowCustomOptionPopup((prev) => !prev)}
        />
      )}
    </ProductDetailWrapper>
  );
};

export default observer(CheckOutItemsPage);
